import React from 'react';
import { MapPin, Navigation, Sun, Sparkles } from 'lucide-react';

/**
 * Компонент для отображения популярных направлений России
 */
const PopularDestinations = ({ 
  russianDestinations, 
  formData, 
  setFormData, 
  seasons 
}) => {
  // Фильтруем только популярные направления для отображения
  const popularDestinations = russianDestinations.slice(0, 8);
  
  // Функция получения информации о сезоне для направления в текущем месяце
  const getSeasonInfo = (destination) => {
    const currentMonth = parseInt(formData.travelMonth, 10);
    
    if (destination.season.peak && destination.season.peak.includes(currentMonth)) {
      return { season: 'peak', icon: <Sparkles className="w-3 h-3 text-amber-500" /> };
    } else if (destination.season.high && destination.season.high.includes(currentMonth)) {
      return { season: 'high', icon: <Sun className="w-3 h-3 text-yellow-500" /> };
    } else if (destination.season.low && destination.season.low.includes(currentMonth)) {
      return { season: 'low', icon: <Sun className="w-3 h-3 text-blue-400" /> };
    } else {
      return { season: 'shoulder', icon: <Sun className="w-3 h-3 text-gray-400" /> };
    }
  };
  
  // Функция для получения текстового описания сезона
  const getSeasonLabel = (seasonType) => {
    const seasonLabels = {
      'peak': 'Пиковый сезон',
      'high': 'Высокий сезон',
      'shoulder': 'Межсезонье',
      'low': 'Низкий сезон'
    };
    return seasonLabels[seasonType] || 'Межсезонье';
  };

  // Обработчик выбора направления
  const handleSelectDestination = (destinationId) => {
    setFormData(prev => ({
      ...prev,
      destination: destinationId
    }));
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
          <Navigation className="w-5 h-5 text-purple-500 mr-2" />
          Популярные направления
        </h2>
        
        <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
          <span className="mr-1">Выбран месяц:</span>
          <span className="font-medium">{getMonthName(formData.travelMonth)}</span>
        </div>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {popularDestinations.map(destination => {
          const isSelected = formData.destination === destination.id;
          const seasonInfo = getSeasonInfo(destination);
          
          return (
            <button
              key={destination.id}
              onClick={() => handleSelectDestination(destination.id)}
              className={`
                relative p-3 rounded-lg border border-gray-200 dark:border-gray-700 
                text-left transition-all duration-200 flex flex-col
                ${isSelected 
                  ? 'bg-purple-50 border-purple-300 dark:bg-purple-900/30 dark:border-purple-700 shadow-sm' 
                  : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750'}
              `}
              aria-pressed={isSelected}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  <MapPin className={`w-4 h-4 ${isSelected ? 'text-purple-600' : 'text-gray-500'} mr-1`} />
                  <span className={`text-sm font-medium ${isSelected ? 'text-purple-700 dark:text-purple-300' : 'text-gray-900 dark:text-gray-100'}`}>
                    {destination.name}
                  </span>
                </div>
                
                {/* Индикатор текущего сезона */}
                <div 
                  className={`
                    flex items-center px-1.5 py-0.5 rounded text-xs font-medium
                    ${seasonInfo.season === 'peak' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300' : ''}
                    ${seasonInfo.season === 'high' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' : ''}
                    ${seasonInfo.season === 'shoulder' ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300' : ''}
                    ${seasonInfo.season === 'low' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300' : ''}
                  `}
                  title={getSeasonLabel(seasonInfo.season)}
                >
                  {seasonInfo.icon}
                  <span className="ml-1">{seasonInfo.season === 'peak' ? 'Пик' : seasonInfo.season === 'high' ? 'Высокий' : seasonInfo.season === 'low' ? 'Низкий' : 'Средний'}</span>
                </div>
              </div>
              
              {/* Информация о типе направления */}
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {destination.type === 'city' && 'Город'}
                {destination.type === 'resort' && 'Курорт'}
                {destination.type === 'nature' && 'Природа'}
                {destination.type === 'route' && 'Маршрут'}
                
                {/* Индикатор стоимости */}
                <span className="ml-2">
                  {destination.costIndex < 0.9 && '$ (бюджетно)'}
                  {destination.costIndex >= 0.9 && destination.costIndex < 1.1 && '$$ (средне)'}
                  {destination.costIndex >= 1.1 && destination.costIndex < 1.3 && '$$$ (дорого)'}
                  {destination.costIndex >= 1.3 && '$$$$ (премиум)'}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      
      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center">
        <span className="mr-4">
          <Sparkles className="w-3 h-3 text-amber-500 inline-block mr-1" /> Пиковый сезон
        </span>
        <span className="mr-4">
          <Sun className="w-3 h-3 text-yellow-500 inline-block mr-1" /> Высокий сезон
        </span>
        <span className="mr-4">
          <Sun className="w-3 h-3 text-gray-400 inline-block mr-1" /> Межсезонье
        </span>
        <span>
          <Sun className="w-3 h-3 text-blue-400 inline-block mr-1" /> Низкий сезон
        </span>
      </div>
    </div>
  );
};

// Функция получения названия месяца
const getMonthName = (monthIndex) => {
  const months = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];
  return months[monthIndex] || 'Неизвестный месяц';
};

export default PopularDestinations;